import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Utensils, Leaf, BookOpen, Recycle, ChartLine, ArrowRight,
  Users, Calendar, Globe, Heart, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const moduleIcons = {
  kitchen: Utensils,
  harvest: Leaf,
  vault: BookOpen,
  cycle: Recycle,
  impact: ChartLine
};

const moduleColors = {
  kitchen: "from-orange-500 to-red-600",
  harvest: "from-emerald-500 to-green-600",
  vault: "from-amber-500 to-yellow-600",
  cycle: "from-teal-500 to-cyan-600",
  impact: "from-purple-500 to-indigo-600"
};

export default function FoodHub() {
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [overviewRes, sessionsRes, recipesRes] = await Promise.all([
          fetch(`${API}/api/food/overview`),
          fetch(`${API}/api/food/kitchen/sessions`),
          fetch(`${API}/api/food/vault`)
        ]);
        
        if (overviewRes.ok) setOverview(await overviewRes.json());
        if (sessionsRes.ok) {
          const data = await sessionsRes.json();
          setSessions(data.sessions?.slice(0, 3) || []);
        }
        if (recipesRes.ok) {
          const data = await recipesRes.json();
          setRecipes(data.recipes?.slice(0, 3) || []);
        }
      } catch (err) {
        console.error('Error fetching food data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-orange-50/30" data-testid="food-hub">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-700 via-red-700 to-orange-800 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-400 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-red-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <Badge className="bg-white/20 text-white mb-4">F4: Food</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Farm2Table
            </h1>
            <p className="text-xl text-orange-100 mb-6 max-w-2xl">
              Feeding yourself and your community is an act of resistance, dignity, and legacy.
              From heritage recipes to garden harvests — build food sovereignty.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/food/kitchen">
                <Button size="lg" className="bg-white text-orange-900 hover:bg-orange-50">
                  <Utensils className="mr-2 h-5 w-5" />
                  Join the Kitchen
                </Button>
              </Link>
              <Link to="/food/vault">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <BookOpen className="mr-2 h-5 w-5" />
                  Recipe Vault
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 space-y-12">
        {/* BOGO Impact Banner */}
        <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <Heart className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Buy One, Give One</h3>
                  <p className="text-emerald-100">
                    Every Premium subscription deploys a Seed & Tool Kit to a family in need.
                  </p>
                </div>
              </div>
              <Link to="/food/subscribe">
                <Button className="bg-white text-emerald-700 hover:bg-emerald-50">
                  Become a Seed Sponsor
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Core Modules */}
        <section>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-stone-800 mb-2">Food Sovereignty Ecosystem</h2>
            <p className="text-stone-600">Education. Access. Legacy.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {overview?.modules?.slice(0, 3).map((module, idx) => {
              const Icon = moduleIcons[module.id] || Utensils;
              const colorClass = moduleColors[module.id] || "from-gray-500 to-gray-600";
              const links = {
                kitchen: "/food/kitchen",
                harvest: "/food/harvest",
                vault: "/food/vault"
              };
              
              return (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link to={links[module.id] || "/food"}>
                    <Card className="h-full hover:shadow-lg transition-all group cursor-pointer border-2 border-transparent hover:border-orange-200">
                      <CardHeader>
                        <div className={`p-4 rounded-xl bg-gradient-to-br ${colorClass} text-white w-fit mb-3 group-hover:scale-110 transition-transform`}>
                          <Icon className="h-6 w-6" />
                        </div>
                        <CardTitle className="text-lg">{module.name}</CardTitle>
                        <CardDescription>{module.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <span className="text-orange-600 text-sm font-medium flex items-center">
                          Explore <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Upcoming Sessions */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-stone-800">Global Kitchen Live</h2>
              <p className="text-stone-600">Join virtual cooking sessions from around the world</p>
            </div>
            <Link to="/food/kitchen">
              <Button variant="outline">View All Sessions</Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {sessions.map((session, idx) => (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={session.tier === "free" ? "secondary" : "default"}>
                        {session.tier === "free" ? "Free" : "Premium"}
                      </Badge>
                      <Badge variant="outline">{session.cultural_focus}</Badge>
                    </div>
                    <CardTitle className="text-lg">{session.title}</CardTitle>
                    <CardDescription>
                      Chef {session.chef.name} • {session.chef.region}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2 text-sm text-stone-500 mb-3">
                      <Calendar className="h-4 w-4" />
                      {formatDate(session.date)}
                    </div>
                    <div className="flex items-center justify-between text-sm text-stone-500 mb-4">
                      <span>{session.registered}/{session.capacity} registered</span>
                    </div>
                    <Link to={`/food/kitchen/${session.id}`}>
                      <Button className="w-full bg-orange-600 hover:bg-orange-700">
                        Register Now
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Featured Recipes */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-stone-800">Heritage Recipe Vault</h2>
              <p className="text-stone-600">Crowdsourced recipes with AI nutritional analysis</p>
            </div>
            <Link to="/food/vault">
              <Button variant="outline">Browse All Recipes</Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {recipes.map((recipe, idx) => (
              <motion.div
                key={recipe.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">{recipe.region}</Badge>
                      <Badge className="bg-emerald-100 text-emerald-700">{recipe.cost_tier.replace('_', ' ')}</Badge>
                    </div>
                    <CardTitle className="text-lg">{recipe.title}</CardTitle>
                    <CardDescription>{recipe.cultural_origin}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-stone-600 mb-3 line-clamp-2">{recipe.family_story}</p>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {recipe.health_tags?.map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                    <Link to={`/food/vault/${recipe.id}`}>
                      <Button variant="outline" className="w-full">
                        View Recipe
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Food Guilds & Seed-to-Table */}
        <section className="grid md:grid-cols-2 gap-8">
          {/* Food Guilds */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-orange-600" />
                Food Guilds
              </CardTitle>
              <CardDescription>Regional communities sharing food traditions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <span className="font-medium">Gulf South / Caribbean</span>
                  <Badge>45 members</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                  <span className="font-medium">West Africa</span>
                  <Badge>32 members</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                  <span className="font-medium">North America (Urban)</span>
                  <Badge>56 members</Badge>
                </div>
              </div>
              <Link to="/food/guilds">
                <Button variant="outline" className="w-full">
                  <Users className="h-4 w-4 mr-2" /> Join a Guild
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Seed-to-Table */}
          <Card className="bg-gradient-to-br from-emerald-600 to-green-700 text-white">
            <CardContent className="py-8 flex flex-col justify-center h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-white/20 rounded-xl">
                  <Leaf className="h-8 w-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Seed-to-Table Cycle</h3>
                  <p className="text-emerald-100">3-phase pathway to food sovereignty</p>
                </div>
              </div>
              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">1</span>
                  <span>GROW — Select crops, start your garden</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">2</span>
                  <span>COOK — Prepare what you've harvested</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">3</span>
                  <span>TEACH — Pass knowledge to a neighbor</span>
                </div>
              </div>
              <Link to="/food/harvest">
                <Button className="bg-white text-emerald-700 hover:bg-emerald-50 w-full">
                  Start Your Journey
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>

        {/* Stats */}
        <section>
          <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
            <CardContent className="py-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-orange-600">{overview?.stats?.recipes_in_vault || 3}+</div>
                  <p className="text-stone-500">Heritage Recipes</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-red-600">{overview?.stats?.upcoming_sessions || 3}</div>
                  <p className="text-stone-500">Upcoming Sessions</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-emerald-600">{overview?.stats?.food_guilds || 8}</div>
                  <p className="text-stone-500">Global Guilds</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-amber-600">{overview?.stats?.total_badges || 10}</div>
                  <p className="text-stone-500">Badges to Earn</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
